/**
 * Inline editor for requirement fields — saves to BVP via backend proxy.
 */

import { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Space, Row, Col, message, Modal } from 'antd';
import { getRequirementSource, listRequirements, updateRequirement, updateRequirementSource, setLinkedPackage, fetchDeliveryStages, fetchCategory2Values } from '@/api/requirements';
import { listPackages } from '@/api/packages';
import { useAuth } from '@/hooks/useAuth';
import { getCategoryOptions, localizeOptions } from '@/constants/requirementOptions';
import { useT } from '@/i18n';
import LdapUserSelect, { type UserOption } from '@/components/users/LdapUserSelect';
import type { Requirement, RequirementUpdate, BSPPackage, DeliveryStageOption } from '@/types';

const { OptGroup } = Select;

// Feature 002: Standard delivery stages (for deduplication)
const STANDARD_STAGES = [
  'BSW01(台架验证)',
  'BSW02(首车闭环)',
  'BSW03(性能优化)',
  'BSW04(预量产)',
  'BSW05(量产锁版)',
];

const STANDARD_STAGE_OPTIONS: DeliveryStageOption[] = STANDARD_STAGES.map(stage => ({
  value: stage,
  label: stage,
}));

const VERIFICATION_METHOD_OPTIONS = [
  { value: '测试', label: '测试' },
  { value: '检查', label: '检查' },
];

const PRIORITY_OPTIONS = [
  { value: '必选', label: '必选' },
  { value: '可选', label: '可选' },
];


interface RequirementEditorProps {
  projectId: number;
  requirement: Requirement;
  onSaved: (updated: Requirement) => void;
  onCancel: () => void;
}

const RequirementEditor: React.FC<RequirementEditorProps> = ({
  projectId,
  requirement,
  onSaved,
  onCancel,
}) => {
  const { t, lang } = useT();
  const [form] = Form.useForm();
  const { isExternal } = useAuth();
  const deliveryStageValue = Form.useWatch('deliveryStage', form);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState<BSPPackage[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [category2Values, setCategory2Values] = useState<string[]>([]);
  const [category2Search, setCategory2Search] = useState('');
  // Feature 002: Dynamic delivery stage options
  const [deliveryStages, setDeliveryStages] = useState<string[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  // Feature 002 US4: Track API failure for retry functionality (T060)
  const [hasApiError, setHasApiError] = useState(false);

  // Source (需求来源) editing
  const [editorSourceType, setEditorSourceType] = useState(requirement.sourceType || 'mainline');
  const [editorCustomerReqIds, setEditorCustomerReqIds] = useState<string[]>(requirement.customerReqIds || []);
  const [sourceSaving, setSourceSaving] = useState(false);

  useEffect(() => {
    if (!isExternal) {
      getRequirementSource(projectId, requirement.reqId).then(res => {
        setEditorSourceType(res.source_type);
        setEditorCustomerReqIds(res.customer_req_ids);
      }).catch(() => {});
    }
  }, [projectId, requirement.reqId, isExternal]);

  useEffect(() => {
    listPackages({ project_id: projectId, page_size: 200 })
      .then((res) => setPackages(res.results))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    fetchCategory2Values(projectId)
      .then((res) => setCategory2Values(res.category2_values))
      .catch(() => setCategory2Values([]));
  }, [projectId]);

  // Feature 002: Fetch project-specific delivery stages
  useEffect(() => {
    setLoadingStages(true);
    setHasApiError(false);
    fetchDeliveryStages(projectId)
      .then((res) => {
        setDeliveryStages(res.delivery_stages);
        setHasApiError(false);
      })
      .catch((err) => {
        console.error('Failed to fetch delivery stages:', err);
        // Feature 002 US4: Error Handling Fallback Strategy (T058, T067)
        // 1. Show non-blocking warning to user (5 seconds, auto-dismiss)
        message.warning(t('无法加载项目阶段，已显示标准阶段'));
        // 2. Graceful Degradation: Set empty array for project-specific stages
        //    - Standard stages (STANDARD_STAGES constant) remain available
        //    - Custom input still works via mode="tags"
        //    - User workflow not blocked, can continue creating/editing requirements
        setDeliveryStages([]);
        // 3. Set error flag to enable manual retry button
        setHasApiError(true);
      })
      .finally(() => {
        setLoadingStages(false);
      });
  }, [projectId, t]);

  // Feature 002 US4: Retry handler for manual stage reload (T060)
  const handleRetryLoadStages = () => {
    setLoadingStages(true);
    setHasApiError(false);
    fetchDeliveryStages(projectId)
      .then((res) => {
        setDeliveryStages(res.delivery_stages);
        setHasApiError(false);
        message.success(t('项目阶段已重新加载'));
      })
      .catch((err) => {
        console.error('Failed to reload delivery stages:', err);
        message.error(t('重新加载失败，请稍后再试'));
        setDeliveryStages([]);
        setHasApiError(true);
      })
      .finally(() => {
        setLoadingStages(false);
      });
  };

  // Feature 002: Build grouped options (T029, T030, T067)
  // Deduplication Logic: Filter out project-specific stages that match standard stages exactly
  // - deliveryStages: Array from API containing all unique stages from project requirements
  // - STANDARD_STAGES: Hardcoded array ['BSW01(台架验证)', 'BSW02(首车闭环)', ...]
  // - This prevents duplicate entries in dropdown (e.g., BSW01 appearing in both groups)
  // - Case-sensitive matching: 'BSW01' !== 'bsw01' (different stages)
  const projectSpecificOptions = deliveryStages
    .filter(stage => !STANDARD_STAGES.includes(stage))
    .map(stage => ({ value: stage, label: stage }));

  const baseCategoryOptions = localizeOptions(getCategoryOptions(requirement.subcategory), t);
  const categoryOptions = categorySearch && !baseCategoryOptions.find(o => o.value === categorySearch)
    ? [...baseCategoryOptions, { value: categorySearch, label: categorySearch }]
    : baseCategoryOptions;

  const baseCategory2Options = category2Values.map(v => ({ value: v, label: v }));
  const category2Options = category2Search && !baseCategory2Options.find(o => o.value === category2Search)
    ? [...baseCategory2Options, { value: category2Search, label: category2Search }]
    : baseCategory2Options;

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { linkedPackageId: newPackageId, ...contentValues } = values;

      // Feature 002 US2: Array-to-String Transformation (T040, T067)
      // Why? Select with mode="tags" returns array (even with maxCount=1)
      // Backend expects string, so extract first element
      // Example: ['BSW01(台架验证)'] → 'BSW01(台架验证)'
      if (Array.isArray(contentValues.deliveryStage)) {
        contentValues.deliveryStage = contentValues.deliveryStage[0] || '';
      }

      // LdapUserSelect (labelInValue) returns {value: id, label: username} — extract the username
      const ownerOption = contentValues.owner as UserOption | null | undefined;
      contentValues.owner = ownerOption?.label || '';

      const contentChanged =
        contentValues.description !== requirement.description ||
        contentValues.deliveryStage !== requirement.deliveryStage ||
        contentValues.deliverables !== requirement.deliverables ||
        contentValues.verificationMethod !== requirement.verificationMethod ||
        contentValues.validationCriteria !== (requirement.validationCriteria || '') ||
        contentValues.attachment !== requirement.attachment ||
        contentValues.priority !== requirement.priority ||
        contentValues.remark !== requirement.remark ||
        contentValues.category !== (requirement.category || '') ||
        contentValues.category2 !== (requirement.category2 || '') ||
        contentValues.owner !== requirement.owner;

      const packageChanged =
        (newPackageId ?? null) !== (requirement.linkedPackageId ?? null);

      // 1. Update content via dispatch/req if content fields changed
      if (contentChanged) {
        const payload: RequirementUpdate = {
          id: requirement.id,
          subcategory: requirement.subcategory,
          ...contentValues,
        };
        const updateResult = await updateRequirement(projectId, requirement.reqId, payload);
        if ((updateResult as any)?.affected_projects?.length) {
          Modal.info({
            title: t('主线同步提示'),
            content: t('主线项目 {projects} 包含该需求，已自动完成更新', { projects: (updateResult as any).affected_projects.join(lang === 'en' ? ', ' : '、') }),
          });
        }
      }

      // 2. Update linked package if changed (backend auto-sets status)
      if (packageChanged) {
        await setLinkedPackage(projectId, requirement.reqId, newPackageId ?? null);
      }

      if (!contentChanged && !packageChanged) {
        message.info(t('没有需要保存的变更'));
        setSaving(false);
        return;
      }

      message.success(t('需求已更新'));

      try {
        const { requirements } = await listRequirements(projectId, requirement.subcategory);
        const freshRequirement = requirements.find((r) => r.reqId === requirement.reqId);

        if (freshRequirement) {
          onSaved(freshRequirement);
        } else {
          let finalStatus = requirement.status;
          if (packageChanged) {
            finalStatus = newPackageId ? 'delivered' : 'released';
          } else if (contentChanged) {
            finalStatus = 'updated';
          }

          const selectedPkg = packages.find((p) => p.id === newPackageId);
          onSaved({
            ...requirement,
            ...contentValues,
            status: finalStatus,
            linkedPackageId: newPackageId ?? null,
            linkedPackageName: selectedPkg?.name ?? null,
          });
        }
      } catch (err) {
        console.error('重新获取需求数据失败:', err);
        let finalStatus = requirement.status;
        if (packageChanged) {
          finalStatus = newPackageId ? 'delivered' : 'released';
        } else if (contentChanged) {
          finalStatus = 'updated';
        }

        const selectedPkg = packages.find((p) => p.id === newPackageId);
        onSaved({
          ...requirement,
          ...contentValues,
          status: finalStatus,
          linkedPackageId: newPackageId ?? null,
          linkedPackageName: selectedPkg?.name ?? null,
        });
      }
    } catch (err: unknown) {
      const httpStatus = (err as any)?.response?.status;
      if (httpStatus === 409) {
        message.error(t('保存冲突: 该需求已被其他用户修改，请刷新后重试'));
      } else {
        const msg = (err as any)?.response?.data?.message || t('保存失败，请重试');
        message.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="small"
      initialValues={{
        description: requirement.description,
        deliveryStage: requirement.deliveryStage ? [requirement.deliveryStage] : [],
        deliverables: requirement.deliverables,
        verificationMethod: requirement.verificationMethod,
        validationCriteria: requirement.validationCriteria || '',
        attachment: requirement.attachment || '',
        priority: requirement.priority || '',
        remark: requirement.remark || '',
        linkedPackageId: requirement.linkedPackageId,
        category: requirement.category || '',
        category2: requirement.category2 || '',
        owner: requirement.owner ? { value: -1, label: requirement.owner } : null,
      }}
    >
      <Form.Item label={t('需求描述')} name="description" style={{ marginBottom: 8 }}>
        <Input.TextArea autoSize={{ minRows: 1 }} />
      </Form.Item>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item label={t('责任人')} name="owner" style={{ marginBottom: 8 }}>
            <LdapUserSelect mode="single" placeholder={t('输入名字搜索')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('一级分类')} name="category" style={{ marginBottom: 8 }}>
            <Select
              placeholder={t('选择或输入一级分类')}
              allowClear
              showSearch
              onSearch={setCategorySearch}
              onSelect={() => setCategorySearch('')}
              filterOption={(input, option) => {
                const label = typeof option?.label === 'string' ? option.label : '';
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {categoryOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('二级分类')} name="category2" style={{ marginBottom: 8 }}>
            <Select
              placeholder={t('选择或输入二级分类（可选）')}
              allowClear
              showSearch
              onSearch={setCategory2Search}
              onSelect={() => setCategory2Search('')}
              filterOption={(input, option) => {
                const label = typeof option?.label === 'string' ? option.label : '';
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {category2Options.map(opt => (
                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label={t('优先级')} name="priority" style={{ marginBottom: 8 }}>
            <Select options={PRIORITY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))} allowClear placeholder={t('选择优先级')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item
            label={t('交付阶段')}
            name="deliveryStage"
            style={{ marginBottom: 8 }}
            rules={[
              {
                validator: async (_, value) => {
                  // Feature 002 US2: 20-Character Validation Logic (T041, T067)
                  // Stage 1: Check if value exists (mode="tags" with maxCount=1 returns array)
                  if (!value || value.length === 0) {
                    return Promise.reject(new Error(t('交付阶段不能为空')));
                  }
                  // Stage 2: Extract string from array (Select mode="tags" wraps value in array)
                  const stageValue = Array.isArray(value) ? value[0] : value;
                  // Stage 3: Validate non-empty after trimming whitespace
                  if (!stageValue || stageValue.trim().length === 0) {
                    return Promise.reject(new Error(t('交付阶段不能为空')));
                  }
                  // Stage 4: Enforce 20-character database limit
                  // Why 20? Backend VARCHAR(20) field limit, prevents database errors
                  if (stageValue.length > 20) {
                    return Promise.reject(new Error(t('交付阶段长度不能超过20个字符（当前：{count}）', { count: stageValue.length })));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={(() => {
              // Feature 002 US2: Character counter for custom delivery stage input (T042)
              const currentValue = Array.isArray(deliveryStageValue) ? deliveryStageValue[0] : deliveryStageValue;
              const length = currentValue?.length || 0;
              return (
                <>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {t('{count}/20 字符 · 只能选择一个阶段', { count: length })}
                  </span>
                  {/* Feature 002 US4: Manual retry button for API failures (T060) */}
                  {hasApiError && (
                    <Button
                      type="link"
                      size="small"
                      onClick={handleRetryLoadStages}
                      loading={loadingStages}
                      style={{ marginLeft: '8px', padding: '0', height: 'auto' }}
                    >
                      {t('重新加载项目阶段')}
                    </Button>
                  )}
                </>
              );
            })()}
          >
            <Select
              mode="tags"
              maxLength={20}
              allowClear
              loading={loadingStages}
              disabled={loadingStages}
              placeholder={t('选择或输入自定义交付阶段')}
              onChange={(value: string[]) => {
                // 单值逻辑：只保留最新选择的值,实现直接替换功能
                if (value.length > 1) {
                  // 有多个值时,只保留最后一个(最新选择的)
                  form.setFieldValue('deliveryStage', [value[value.length - 1]]);
                } else {
                  // 单个值或空值,直接设置
                  form.setFieldValue('deliveryStage', value);
                }
              }}
            >
              <OptGroup label={t('标准阶段')}>
                {STANDARD_STAGE_OPTIONS.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </OptGroup>
              {projectSpecificOptions.length > 0 && (
                <OptGroup label={t('项目自定义阶段')}>
                  {projectSpecificOptions.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </OptGroup>
              )}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('交付物')} name="deliverables" style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('验证方法')} name="verificationMethod" style={{ marginBottom: 8 }}>
            <Select options={VERIFICATION_METHOD_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))} allowClear />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label={t('验证准则')} name="validationCriteria" style={{ marginBottom: 8 }}>
        <Input.TextArea
          autoSize={{ minRows: 1 }}
          placeholder={t('请输入验证准则')}
          maxLength={5000}
          showCount
        />
      </Form.Item>
      <Form.Item label={t('设计文档')} name="attachment" style={{ marginBottom: 8 }}>
        <Input
          placeholder={t('请输入设计文档链接（例如：https://example.com/doc.pdf）')}
          allowClear
        />
      </Form.Item>
      <Form.Item label={t('备注')} name="remark" style={{ marginBottom: 8 }}>
        <Input.TextArea
          autoSize={{ minRows: 1 }}
          placeholder={t('请输入需求备注信息（可选）')}
          maxLength={5000}
          showCount
        />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label={t('需求交付首版软件')} name="linkedPackageId" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              placeholder={t('选择关联的底软包')}
              optionFilterProp="label"
              options={packages.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
        </Col>
        {!isExternal && (
          <Col span={12}>
            <Form.Item label={t('需求来源')} style={{ marginBottom: 8 }}>
              <Space orientation="vertical" style={{ width: '100%' }}>
                <Select
                  size="small"
                  value={editorSourceType}
                  style={{ minWidth: 160 }}
                  onChange={(val) => {
                    setEditorSourceType(val);
                    if (val === 'mainline') setEditorCustomerReqIds([]);
                  }}
                  options={[
                    { value: 'mainline', label: t('主线') },
                    { value: 'customer', label: t('客户原始需求') },
                  ]}
                />
                {editorSourceType === 'customer' && (
                  <Select
                    mode="tags"
                    size="small"
                    placeholder={t('输入客户原始需求ID，回车确认')}
                    value={editorCustomerReqIds}
                    onChange={(vals) => setEditorCustomerReqIds(vals)}
                    style={{ minWidth: 200 }}
                    tokenSeparators={[',', ' ']}
                  />
                )}
                <Button
                  size="small"
                  loading={sourceSaving}
                  onClick={async () => {
                    setSourceSaving(true);
                    try {
                      await updateRequirementSource(projectId, requirement.reqId, {
                        source_type: editorSourceType,
                        customer_req_ids: editorSourceType === 'customer' ? editorCustomerReqIds : [],
                      });
                      message.success(t('需求来源已更新'));
                    } catch {
                      message.error(t('需求来源保存失败'));
                    } finally {
                      setSourceSaving(false);
                    }
                  }}
                >
                  {t('单独保存需求来源')}
                </Button>
              </Space>
            </Form.Item>
          </Col>
        )}
      </Row>
      <Space>
        <Button type="primary" onClick={handleSave} loading={saving}>
          {t('保存')}
        </Button>
        <Button onClick={onCancel}>{t('取消')}</Button>
      </Space>
    </Form>
  );
};

export default RequirementEditor;
